import type { ButtonHTMLAttributes } from 'react';

import type { IconMapping } from '@onyx/ui/components/colleague/icon';
import { Icon } from '@onyx/ui/components/colleague/icon';
import { getClassName } from '@onyx/ui/style-utils';

import styles from './button.module.css';

export type ButtonVariant = 'filled' | 'outlined' | 'text' | 'destructive';

export type ButtonProps = Pick<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'id' | 'onClick' | 'onFocus' | 'onBlur' | 'onKeyDown' | 'onKeyUp' | 'type'
> & {
  children?: React.ReactNode;
  variant?: ButtonVariant;
  icon?: IconMapping;
  isDisabled?: boolean;
  isInverse?: boolean;
  isLoading?: boolean;
  className?: string;
};

export const Button = ({
  children,
  variant = 'filled',
  icon,
  isDisabled = false,
  isInverse = false,
  isLoading = false,
  onClick,
  type = 'button',
  className,
  ...rest
}: ButtonProps) => {
  const variantMap: Record<ButtonVariant, string> = {
    filled: styles.variantFilled,
    outlined: styles.variantOutlined,
    text: styles.variantText,
    destructive: styles.variantDestructive,
  };

  const isInverseAllowed = variant !== 'destructive' && isInverse;

  const classNames = getClassName([
    styles.button,
    variantMap[variant],
    isInverseAllowed && styles.inverse,
    isLoading && styles.loading,
    className,
  ]);

  const displayIconName = isLoading ? 'Sync' : icon;
  const iconComponent = displayIconName ? (
    <Icon
      name={displayIconName}
      size={18}
      className={isLoading ? styles.loadingIcon : undefined}
    />
  ) : null;

  return (
    <button
      type={type}
      className={classNames}
      onClick={isDisabled ? undefined : onClick}
      disabled={isDisabled}
      aria-disabled={isDisabled}
      {...rest}
    >
      {iconComponent}
      <span className={styles.label}>{children}</span>
    </button>
  );
};
